// src/utils/offlineDB.js

const DB_NAME = 'ArcheryNexusOffline';
const DB_VERSION = 3;

export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => reject('IndexedDB hiba: ' + event.target.errorCode);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Verseny adatok tárolója
      if (!db.objectStoreNames.contains('competitions')) {
        db.createObjectStore('competitions', { keyPath: 'id' });
      }

      // Nevezők tárolója (versenyId indexszel)
      if (!db.objectStoreNames.contains('participants')) {
        const store = db.createObjectStore('participants', { keyPath: 'id' });
        store.createIndex('competitionId', 'competitionId', { unique: false });
      }

      // Függőben lévő eredmények (szinkronizáláshoz)
      if (!db.objectStoreNames.contains('pendingScores')) {
        const store = db.createObjectStore('pendingScores', { keyPath: 'id' });
        store.createIndex('competitionId', 'competitionId', { unique: false });
      }

      // ÚJ: Függőben lévő verseny-módosítások (pl. regisztrációs zár)
      if (!db.objectStoreNames.contains('pendingCompetitionUpdates')) {
        db.createObjectStore('pendingCompetitionUpdates', { keyPath: 'id' });
      }

      // ÚJ: Perzisztens törölt résztvevők tárolója (Zombi védelem)
      if (!db.objectStoreNames.contains('deletedParticipants')) {
        const store = db.createObjectStore('deletedParticipants', { keyPath: 'id' });
        store.createIndex('competitionId', 'competitionId', { unique: false });
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
  });
};

// VERSENY ADATOK MENTÉSE (Teljes szinkronizációkor kitakarítva a régi felhalmozódott résztvevőket, feketelistát és függőben lévő törléseket)
export const saveCompetitionOffline = async (competition, participants) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions', 'participants', 'deletedParticipants', 'pendingScores'], 'readwrite');
    const compStore = transaction.objectStore('competitions');
    const partStore = transaction.objectStore('participants');
    const delStore = transaction.objectStore('deletedParticipants');
    const pendingStore = transaction.objectStore('pendingScores');

    const delIndex = delStore.index('competitionId');
    const delRequest = delIndex.getAll(competition.id);

    const pendingIndex = pendingStore.index('competitionId');
    const pendingReq = pendingIndex.getAll(competition.id);

    const partIndex = partStore.index('competitionId');
    const partKeysReq = partIndex.getAllKeys(competition.id);

    compStore.put({ ...competition, isOfflineDownloaded: true, offlineDownloadedAt: Date.now() });

    // 1. Kitakarítjuk a felhalmozódott régi helyi résztvevőket erről a versenyről
    partKeysReq.onsuccess = () => {
      const oldKeys = partKeysReq.result || [];
      oldKeys.forEach(key => {
        partStore.delete(key);
      });

      // 2. Elmentjük a friss felhős résztvevőket
      participants.forEach(p => {
        partStore.put({ ...p, competitionId: competition.id });
      });
    };

    delRequest.onsuccess = () => {
      const oldDeleted = delRequest.result || [];
      oldDeleted.forEach(item => {
        delStore.delete(item.id);
      });
    };

    pendingReq.onsuccess = () => {
      const oldPending = pendingReq.result || [];
      oldPending.forEach(pItem => {
        if (pItem.type === 'delete_participant') {
          pendingStore.delete(pItem.id);
        }
      });
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// VERSENY ADATOK LEKÉRÉSE (Perzisztens és pending törlések szűrésével)
export const getCompetitionOffline = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions', 'participants', 'pendingScores', 'deletedParticipants'], 'readonly');
    const compRequest = transaction.objectStore('competitions').get(id);
    const partIndex = transaction.objectStore('participants').index('competitionId');
    const partRequest = partIndex.getAll(id);
    const pendingRequest = transaction.objectStore('pendingScores').getAll();
    const delIndex = transaction.objectStore('deletedParticipants').index('competitionId');
    const delRequest = delIndex.getAll(id);

    transaction.oncomplete = () => {
      const pendingDeleted = (pendingRequest.result || [])
        .filter(item => item.type === 'delete_participant')
        .map(item => item.id);
      const persistentDeleted = (delRequest.result || []).map(item => item.id);
      const deletedIds = new Set([...pendingDeleted, ...persistentDeleted]);

      const validParticipants = (partRequest.result || []).filter(p => p && p.fullName && !deletedIds.has(p.id));
      resolve({
        competition: compRequest.result,
        participants: validParticipants
      });
    };
    transaction.onerror = () => reject(transaction.error);
  });
};

// EREDMÉNY MENTÉSE (OFFLINE)
export const saveScoreOffline = async (competitionId, participantId, scoreData) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingScores', 'participants'], 'readwrite');
    const pendingStore = transaction.objectStore('pendingScores');
    const partStore = transaction.objectStore('participants');

    pendingStore.put({
      id: participantId,
      competitionId,
      ...scoreData,
      timestamp: Date.now()
    });

    const getReq = partStore.get(participantId);
    getReq.onsuccess = () => {
      const existing = getReq.result;
      if (existing) {
        const mergedData = { ...existing, ...scoreData };
        if (scoreData.shootOffRank === null) delete mergedData.shootOffRank;
        partStore.put(mergedData);
      }
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// FÜGGŐBEN LÉVŐ EREDMÉNYEK LEKÉRÉSE
export const getPendingScores = async (competitionId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingScores'], 'readonly');
    const index = transaction.objectStore('pendingScores').index('competitionId');
    const request = index.getAll(competitionId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// SIKERES SZINKRON UTÁNI TÖRLÉS
export const clearPendingScore = async (participantId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingScores'], 'readwrite');
    transaction.objectStore('pendingScores').delete(participantId);
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// ÚJ NEVEZŐ RÖGZÍTÉSE (OFFLINE) - Ha újraregisztrálnak valakit, töröljük a perzisztens töröltek közül is
export const addParticipantOffline = async (competitionId, participantData) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants', 'pendingScores', 'deletedParticipants'], 'readwrite');
    const partStore = transaction.objectStore('participants');
    const pendingStore = transaction.objectStore('pendingScores');
    const delStore = transaction.objectStore('deletedParticipants');

    const tempId = participantData.id || `temp_${Date.now()}`;
    const data = {
        ...participantData,
        id: tempId,
        competitionId,
        isOfflineNew: true
    };

    delStore.delete(tempId);

    partStore.put(data);
    pendingStore.put({ ...data, type: 'new_participant' });

    transaction.oncomplete = () => resolve(data);
    transaction.onerror = () => reject(transaction.error);
  });
};

// NEVEZŐ ADATAINAK FRISSÍTÉSE (OFFLINE) - GOLYÓÁLLÓ MEZŐMEGŐRZÉSEL
export const updateParticipantOffline = async (participantId, updateData, currentFullParticipantObject = null) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants', 'pendingScores'], 'readwrite');
    const partStore = transaction.objectStore('participants');
    const pendingStore = transaction.objectStore('pendingScores');

    const request = partStore.get(participantId);
    request.onsuccess = () => {
      const existing = request.result || currentFullParticipantObject;
      if (existing && existing.fullName) {
        const updatedParticipant = { ...existing, ...updateData };
        partStore.put(updatedParticipant);

        pendingStore.put({
          id: participantId,
          competitionId: updatedParticipant.competitionId,
          type: updatedParticipant.isOfflineNew ? 'new_participant' : 'update_participant',
          updateData: updateData,
          timestamp: Date.now()
        });
      }
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// NEVEZŐ TÖRLÉSE (OFFLINE) - Perzisztens rögzítéssel a deletedParticipants-ban
export const deleteParticipantOffline = async (participantId, competitionId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants', 'pendingScores', 'deletedParticipants'], 'readwrite');
    const partStore = transaction.objectStore('participants');
    const pendingStore = transaction.objectStore('pendingScores');
    const delStore = transaction.objectStore('deletedParticipants');

    partStore.delete(participantId);
    if (competitionId) {
      delStore.put({ id: participantId, competitionId, deletedAt: Date.now() });
    } else {
      delStore.put({ id: participantId, competitionId: 'unknown', deletedAt: Date.now() });
    }

    if (participantId.toString().startsWith('temp_')) {
      pendingStore.delete(participantId);
    } else {
      pendingStore.put({ id: participantId, competitionId, type: 'delete_participant', timestamp: Date.now() });
    }

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// ÖSSZES OFFLINE VERSENY LEKÉRÉSE (Dashboardhoz)
export const getAllCompetitionsOffline = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions'], 'readonly');
    const store = transaction.objectStore('competitions');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// VERSENYEK GYORSÍTÓTÁRAZÁSA (AUTOMATA MENTÉS + REJTETT/TÖRÖLT ELEMEK SZINKRONIZÁLÁSA + JELVÉNY MEGŐRZÉSE)
export const cacheCompetitions = async (competitions) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions'], 'readwrite');
    const store = transaction.objectStore('competitions');

    const getAllReq = store.getAll();
    getAllReq.onsuccess = () => {
      const serverIds = new Set(competitions.map(c => c.id));
      const existing = getAllReq.result || [];
      const existingMap = new Map(existing.map(c => [c.id, c]));

      // Töröljük a helyi tárolóból azokat az aktív tesztversenyeket, amik a szerverről már végleg törölve lettek
      existing.forEach(oldComp => {
        if (oldComp.status !== 'Ready to Upload' && oldComp.status !== 'Lezárt' && !serverIds.has(oldComp.id)) {
          store.delete(oldComp.id);
        }
      });

      competitions.forEach(comp => {
        const oldComp = existingMap.get(comp.id);
        const isDownloaded = comp.isOfflineDownloaded || oldComp?.isOfflineDownloaded || false;
        const downloadedAt = comp.offlineDownloadedAt || oldComp?.offlineDownloadedAt || null;

        store.put({
          ...comp,
          ...(isDownloaded ? { isOfflineDownloaded: true, offlineDownloadedAt: downloadedAt } : {})
        });
      });
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// VERSENY TÖRLÉSE OFFLINE MEMÓRIÁBÓL
export const deleteCompetitionOffline = async (competitionId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['competitions'], 'readwrite');
    const store = transaction.objectStore('competitions');
    store.delete(competitionId);
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// HELYI NEVEZŐK SZÁMÁNAK LEKÉRÉSE
export const getOfflineParticipantsCount = async (competitionId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['participants', 'deletedParticipants', 'pendingScores'], 'readonly');
    const partIndex = transaction.objectStore('participants').index('competitionId');
    const delIndex = transaction.objectStore('deletedParticipants').index('competitionId');
    const pendingStore = transaction.objectStore('pendingScores');

    const partReq = partIndex.getAll(competitionId);
    const delReq = delIndex.getAll(competitionId);
    const pendingReq = pendingStore.getAll();

    transaction.oncomplete = () => {
      const deletedIds = new Set([
        ...(delReq.result || []).map(item => item.id),
        ...(pendingReq.result || []).filter(item => item.type === 'delete_participant').map(item => item.id)
      ]);
      const validCount = (partReq.result || []).filter(p => !deletedIds.has(p.id)).length;
      resolve(validCount);
    };
    transaction.onerror = (e) => reject(transaction.error || e);
  });
};

// VERSENY ALAPADATOK MÓDOSÍTÁSÁNAK VÁRÓLISTÁra TÉTELE
export const saveCompetitionUpdateOffline = async (competitionId, updateData) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingCompetitionUpdates', 'competitions'], 'readwrite');
    const pendingStore = transaction.objectStore('pendingCompetitionUpdates');
    const compStore = transaction.objectStore('competitions');

    pendingStore.put({
      id: competitionId,
      ...updateData,
      timestamp: Date.now()
    });

    const getReq = compStore.get(competitionId);
    getReq.onsuccess = () => {
      if (getReq.result) {
        compStore.put({ ...getReq.result, ...updateData });
      }
    };

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getCompetitionUpdatesOffline = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingCompetitionUpdates'], 'readonly');
    const store = transaction.objectStore('pendingCompetitionUpdates');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

export const getPendingCompetitionUpdates = getCompetitionUpdatesOffline;

export const clearPendingCompetitionUpdate = async (competitionId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingCompetitionUpdates'], 'readwrite');
    transaction.objectStore('pendingCompetitionUpdates').delete(competitionId);
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};
